/**
 * settingsLayers.js — per-widget windowed-vs-maximized settings layers (#3).
 *
 * Storage shape on `widget.settings`:
 *   {
 *     foo: 50,                       // root value (used by both layers)
 *     windowed:  { foo: 25 },        // override when window is *not* maximized
 *     maximized: { foo: 200 },       // override when window is maximized
 *   }
 *
 * `pickSetting(settings, key, isMaximized)` resolves a single key with the
 * fallback chain:
 *   1. layer-specific value (if defined)
 *   2. root value (if defined)
 *   3. undefined  (caller picks its own default)
 *
 * Schemas in settingsSchemas.js can flag a field with `scopable: true` to
 * indicate the panel should let the user supply distinct values per
 * layer. Fields without `scopable: true` are treated as root-only.
 */

const LAYERS = ['root', 'windowed', 'maximized'];
export { LAYERS };

export function pickSetting(settings, key, isMaximized) {
  if (!settings || typeof settings !== 'object') return undefined;
  const layer = isMaximized ? 'maximized' : 'windowed';
  const lyr = settings[layer];
  if (lyr && Object.prototype.hasOwnProperty.call(lyr, key)) return lyr[key];
  if (Object.prototype.hasOwnProperty.call(settings, key)) return settings[key];
  return undefined;
}

/** Read a scalar with a default fallback. */
export function pickSettingOr(settings, key, isMaximized, fallback) {
  const v = pickSetting(settings, key, isMaximized);
  return v === undefined ? fallback : v;
}

/**
 * Apply a layered patch — given the current settings, a layer name, and a
 * { key: value } patch, return the next settings object. `value === undefined`
 * removes the override at that layer (so the root value re-takes effect).
 */
export function patchLayer(settings, layer, patch) {
  const next = { ...(settings || {}) };
  if (layer === 'root') {
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) delete next[k];
      else next[k] = v;
    }
    return next;
  }
  const cur = { ...(next[layer] || {}) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete cur[k];
    else cur[k] = v;
  }
  if (Object.keys(cur).length === 0) {
    delete next[layer];
  } else {
    next[layer] = cur;
  }
  return next;
}
